"use client";

import { useCallback, useEffect, useRef, useState } from "react";
const DISMISS_TIMEOUT_MS = 5000;
interface UseHomeItemDismissOptions {
  hideOnMoveIds: number[];
  focusedHideOnMoveId: number | null;
}
export function useHomeItemDismiss({
  hideOnMoveIds,
  focusedHideOnMoveId
}: UseHomeItemDismissOptions) {
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideOnMoveIdsRef = useRef(hideOnMoveIds);
  hideOnMoveIdsRef.current = hideOnMoveIds;
  const dismissIds = useCallback((ids: number[]) => {
    if (ids.length === 0) return;
    setDismissedIds(prev => {
      const added = ids.filter(id => !prev.includes(id));
      return added.length > 0 ? prev.concat(added) : prev;
    });
  }, []);
  const startDismissTimer = useCallback(() => {
    if (timeoutRef.current) return;
    timeoutRef.current = setTimeout(() => {
      dismissIds(hideOnMoveIdsRef.current);
      timeoutRef.current = null;
    }, DISMISS_TIMEOUT_MS);
  }, [dismissIds]);
  useEffect(() => {
    const handleInteraction = () => {
      startDismissTimer();
    };
    window.addEventListener("touchstart", handleInteraction, {
      once: true,
      passive: true
    });
    window.addEventListener("mousedown", handleInteraction, {
      once: true
    });
    return () => {
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("mousedown", handleInteraction);
    };
  }, [startDismissTimer]);
  useEffect(() => {
    if (focusedHideOnMoveId == null) return;
    dismissIds([focusedHideOnMoveId]);
  }, [focusedHideOnMoveId, dismissIds]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);
  return {
    isItemDismissed: (id_0: number) => dismissedIds.includes(id_0)
  };
}